/**
 * Apply a deal WebSocket event to an in-memory deals list (no full reload).
 */

import { mapDealFromApi, normalizeStageFromApi } from '@vanilla/services/crm/ApiEntityMapper.js';
import { computeDealCompleteness } from '@vanilla/utils/KanbanDealSort.js';
import { softCheckRealtimeDeal } from '../schemas/deal.js';

/** Map tiny WS `data` onto FE deal field names. */
export function patchFieldsFromDealWs(data) {
    if (!data || typeof data !== 'object') return {};
    const stageRaw = data.new_stage ?? data.deal_stage ?? data.dealStage;
    const dealStage = stageRaw != null ? normalizeStageFromApi(stageRaw) : undefined;
    const amountRaw = data.contract_value ?? data.contractValue;
    const amount = amountRaw != null ? Number(amountRaw) : undefined;

    const patch = {};
    if (data.deal_name != null || data.dealName != null) {
        patch.dealName = data.deal_name ?? data.dealName;
        patch.name = patch.dealName;
    }
    if (dealStage != null) {
        patch.dealStage = dealStage;
        patch.deal_stage = dealStage;
        if (dealStage === 'closed') patch.status = 'won';
        else if (dealStage === 'closedLost') patch.status = 'lost';
    }
    if (data.status != null) patch.status = data.status;
    if (amount != null && !Number.isNaN(amount)) {
        patch.contractValue = amount;
        patch.amount = amount;
        patch.dealAmount = amount;
    }
    if (data.updated_at || data.updatedAt) patch.updatedAt = data.updated_at || data.updatedAt;
    if (data.updated_by || data.updatedBy) patch.updatedBy = data.updated_by || data.updatedBy;
    return patch;
}

function withDerived(row) {
    const amt = row.contractValue || row.dealAmount || row.amount || 0;
    const prob = row.probability ?? 0;
    return {
        ...row,
        amount: amt,
        dealAmount: amt,
        forecastAmount: Math.round(amt * prob / 100),
        completenessScore: computeDealCompleteness(row),
    };
}

/**
 * @param {Array} deals
 * @param {{ eventType?: string, action?: string, entity_id?: string, data?: object }} msg
 * @returns {{ deals: Array, fetchId: string|null }}
 */
export function applyDealRealtimePatch(deals, msg = {}) {
    softCheckRealtimeDeal(msg);
    const list = Array.isArray(deals) ? deals : [];
    const eventType = msg.eventType || '';
    const id = String(msg.entity_id || msg.data?.id || '');
    if (!id) return { deals: list, fetchId: null };

    if (eventType === 'deal.deleted' || msg.action === 'delete') {
        return { deals: list.filter((d) => String(d.id) !== id), fetchId: null };
    }

    const idx = list.findIndex((d) => String(d.id) === id);
    const fields = patchFieldsFromDealWs(msg.data || {});

    if (eventType === 'deal.created' || msg.action === 'create') {
        if (idx >= 0) {
            const next = list.slice();
            next[idx] = withDerived({ ...next[idx], ...fields });
            return { deals: next, fetchId: null };
        }
        return { deals: list, fetchId: id };
    }

    if (idx < 0) {
        if (eventType === 'deal.updated' || eventType === 'deal.stage_changed') {
            return { deals: list, fetchId: id };
        }
        return { deals: list, fetchId: null };
    }

    const next = list.slice();
    next[idx] = withDerived({ ...next[idx], ...fields });
    return { deals: next, fetchId: null };
}

/** Merge delta list-view rows into an existing list by id. */
export function mergeDealRowsById(existing, incoming) {
    const map = new Map((existing || []).map((d) => [String(d.id), d]));
    for (const row of incoming || []) {
        if (!row?.id) continue;
        const id = String(row.id);
        const prev = map.get(id);
        map.set(id, prev ? withDerived({ ...prev, ...row }) : withDerived(row));
    }
    return Array.from(map.values());
}

/** Turn a raw API deal into a list-row shape for insert after create. */
export function dealRowFromApiPayload(raw) {
    const mapped = mapDealFromApi(raw);
    if (!mapped) return null;
    return withDerived(mapped);
}
